'use client';

import { useState, useEffect } from 'react';
import { Wizard } from '../wizard/wizard';
import { Label } from '../label/label';
import { Button } from '../button/button';
import { CodeInput } from '../code-input/code-input';
import { isValidPhoneNumber, PhoneInput } from '../phone-input/phone-input';
import { Text } from '../text/text';
import { Progress } from '../progress/progress';
import { showContainer, usePhoneSignIn } from '@instamenu/context-firebase';
import { useUserContext } from '@instamenu/context-user';
import { formatPhone } from '@instamenu/formatters';
import posthog from 'posthog-js';
import { Flex } from '../flex/flex';

export function InlineAuth({ onSuccess = () => null }: { onSuccess?: () => void }) {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const { isAuthenticated } = useUserContext();
  const { sendCodeToPhone, confirmCode, loading, error } = usePhoneSignIn();
  const [step, setStep] = useState<'phone' | 'code'>('phone');

  useEffect(() => showContainer(), []);

  const handlePhoneNumberChange = async (value: string) => {
    setPhoneNumber(value);

    if (isValidPhoneNumber(value)) {
      if (await sendCodeToPhone(`+1${value}`)) {
        setStep('code');
      }
    }
  };

  const handleResendCode = async () => {
    if (isValidPhoneNumber(phoneNumber)) {
      await sendCodeToPhone(`+1${phoneNumber}`);
      setCode('');
    }
  };

  const handleSetCode = async (v: string) => {
    setCode(v);
    if (v.length === 6) {
      const res = await confirmCode(v);
      if (res) {
        onSuccess();
        posthog.capture('phone-sign-in', {
          phone_number: phoneNumber,
        });
      } else {
        setCode('');
      }
    }
  };

  return isAuthenticated ? null : (
    <>
      {error ? <Text error>{error}</Text> : null}
      <Wizard>
        {() =>
          step === 'phone' ? (
            <div>
              <Label>Phone Number (required)</Label>
              <PhoneInput
                name="phoneNumber"
                value={phoneNumber}
                onChange={(v) => handlePhoneNumberChange(v)}
                loading={loading}
              />
            </div>
          ) : step === 'code' ? (
            <div>
              <Label>Enter the code we sent to {formatPhone(phoneNumber)}.</Label>
              <div style={{ justifyContent: 'center', display: 'flex' }}>
                {loading ? (
                  <Progress size={32} />
                ) : (
                  <CodeInput length={6} value={code} onChange={handleSetCode} />
                )}
              </div>
              <Flex style={{ justifyContent: 'space-between' }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setCode('');
                    setStep('phone');
                  }}
                >
                  Back
                </Button>
                <Button variant="text" onClick={handleResendCode}>
                  Resend Code
                </Button>
              </Flex>
            </div>
          ) : null
        }
      </Wizard>
    </>
  );
}
